import { ApolloError, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { ColumnFilterPopover, DataTable } from '@gamma/ui/data-table';
import FrameInspectSharpIcon from '@gamma/ui/icons/FrameInspectSharpIcon';
import InfoSharpIcon from '@gamma/ui/icons/InfoSharpIcon';
import { FlushPanelContent, Panel } from '@gamma/ui/layout';
import { RouterLink } from '@gamma/ui/navigation';
import { GraphQLReqStatus, LoadingSpinner } from '@gamma/ui/progress';
import moment from 'moment';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router';
import { SortingRule, Column as TableColumn, TableState } from 'react-table';
import { TimestampCell } from '@investigator/shared/components';
import {
  graphqlErrorRedirects,
  ROUTES,
  MISSING_DATA_PLACEHOLDER,
} from '@investigator/shared/constants';
import {
  useDateRangeQueryString,
  useIsNewSearch,
  useManualPagination,
  useUserSession,
} from '@investigator/shared/hooks';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  SlideDrawerContext,
} from '@investigator/shared/providers';
import { EntityAnalysisPopover } from '../../../../../../../../../entities/components';
import {
  AlertSummary,
  AnomalyAlert,
  IQueryAlertsPaginated,
  QUERY_ALERTS_ANOMALY_PAGINATED,
  QUERY_ALERTS_ML_PAGINATED,
  QUERY_ALERTS_NOTICE_PAGINATED,
  QUERY_ALERTS_SEARCH_BASED_PAGINATED,
  QUERY_ALERTS_SURICATA_PAGINATED,
  QUERY_ALERTS_YARA_PAGINATED,
  YaraAlert,
} from '../../../../../../../../queries';
import { getDetectionSummary } from '@investigator/shared/utils';
import {
  DetectionAlertsFilters,
  IDetectionContext,
} from '../../../../../../../Detection/Detection';
import {
  AlertRowSplitAnomaly,
  AlertRowSplitML,
  AlertRowSplitNotice,
  AlertRowSplitSearchBased,
  AlertRowSplitSuricata,
  AlertRowSplitYara,
  AnomalyButtonsCol,
  MLButtonsCol,
  NoticeButtonsCol,
  SuricataButtonsCol,
  YaraButtonsCol,
} from './Components';
import {
  Alert,
  AlertEntity,
  CustomSearchRuleAlert,
  Detection,
  DetectionSummary,
  MlAlert,
  MlModels,
  NoticeAlert,
  SuricataAlert,
} from '@investigator/shared/generated/graphql';

export type SanitizedAnomalyAlert = {
  is_custom_severity: boolean;
  severity: number;
  alert_id: string;
  uid: string;
  alert_info: Alert['alert_info'];
  alert_entity: Alert['alert_entity'];
  alert_timestamp: Alert['alert_timestamp'];
  source_entities?: AlertEntity[];
  destination_entities?: AlertEntity[];
  timestamp: number;
  path: string;
  system_name: string;
  orig_h: string;
  source_ip: string;
  usecase: string;
  usecase_description: string;
  anomaly_type: string;
  tenant: string;
};

export const DetectionAlertsFooter = ({
  loading,
  error,
  data,
  signOut,
}: {
  loading: boolean;
  error: ApolloError | undefined;
  data: Alert[] | undefined;
  signOut: () => Promise<void>;
}) => {
  if (loading || error) {
    return (
      <Panel>
        <GraphQLReqStatus
          loading={loading}
          error={error}
          signOut={signOut}
          extended={graphqlErrorRedirects}
        />
      </Panel>
    );
  }
  if (!data?.length) {
    return <Panel>{i18n.pages.alerts.na}</Panel>;
  } else {
    return null;
  }
};

interface AlertEntityPopoverProps {
  entities?: AlertEntity[];
  detectionSummary?: DetectionSummary;
  start: number;
  end: number;
}

export const AlertEntityPopover = ({
  entities,
  detectionSummary,
  start,
  end,
}: AlertEntityPopoverProps) => {
  const { entity_category, entity_id, entity_name, entity_type } =
    entities?.[0] || {};

  // If no detectionSummary provided, return early
  if (!detectionSummary) {
    return entities && entities?.length > 0 ? (
      <Box ml={2}>{entity_name || MISSING_DATA_PLACEHOLDER}</Box>
    ) : (
      <Box ml={2}>{MISSING_DATA_PLACEHOLDER}</Box>
    );
  }

  // Use factory to create normalized DetectionSummary with entity override
  const normalizedSummary = getDetectionSummary(
    detectionSummary,
    entities?.[0],
  );

  return entities && entities?.length === 1 ? (
    entity_id && entity_name && entity_type && entity_category && (
      <EntityAnalysisPopover
        detectionSummary={normalizedSummary}
        start={start}
        end={end}
      />
    )
  ) : entities && entities?.length > 1 ? (
    <HStack>
      {entity_id && entity_name && entity_type && entity_category && (
        <EntityAnalysisPopover
          detectionSummary={normalizedSummary}
          start={start}
          end={end}
        />
      )}
      <Popover trigger="hover" placement="top">
        <PopoverTrigger>
          <Text color="text.link">+{entities?.length - 1}</Text>
        </PopoverTrigger>
        <PopoverContent>
          <PopoverArrow />
          <PopoverBody>
            <VStack alignItems="start">
              {entities
                ?.slice(1, entities?.length)
                .map((entity) => (
                  <Text key={entity?.entity_id}>{entity?.entity_name}</Text>
                ))}
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </HStack>
  ) : (
    <Box ml={2}>---</Box>
  );
};

export type SanitizedMlAlert = {
  alert_timestamp: Alert['alert_timestamp'];
  alert_info: Alert['alert_info'];
  alert_entity: Alert['alert_entity'];
  pipeline: string;
  models: MlModels[];
  timestamp: number;
  logscale_query: string;
  source_entities?: AlertEntity[];
  destination_entities?: AlertEntity[];
  related_entities: AlertEntity[];
  average_technique_score_map: string;
};

export type SanitizedSearchBasedAlert = {
  alert_info: Alert['alert_info'];
  alert_entity: Alert['alert_entity'];
  alert_timestamp: Alert['alert_timestamp'];
  source_entities?: AlertEntity[];
  destination_entities?: AlertEntity[];
  timestamp: number;
};

export type SanitizedNoticeAlert = {
  uid: string;
  alert_id: string;
  msg?: string;
  alert_info: Alert['alert_info'];
  alert_entity: Alert['alert_entity'];
  alert_timestamp: Alert['alert_timestamp'];
  source_entities?: AlertEntity[];
  destination_entities?: AlertEntity[];
  timestamp?: number;
  logscale_query?: string;
  tenant?: string;
};

export type SanitizedSuricataAlert = {
  uid: string;
  alert_id: string;
  msg?: string;
  alert_info: Alert['alert_info'];
  alert_entity: Alert['alert_entity'];
  alert_timestamp: Alert['alert_timestamp'];
  source_entities?: AlertEntity[];
  destination_entities?: AlertEntity[];
  timestamp?: number;
  logscale_query?: string;
  community_id?: string;
  destination_ip?: string;
  payload?: string;
  tenant?: string;
};

export type SanitizedYaraAlert = {
  uid: string;
  alert_id: string;
  alert_info: Alert['alert_info'];
  alert_entity: Alert['alert_entity'];
  alert_timestamp: Alert['alert_timestamp'];
  source_entities?: AlertEntity[];
  destination_entities?: AlertEntity[];
  timestamp: number;
  logscale_query: string;
  match_meta?: string[];
  tenant?: string;
};

interface DetectionAlertsProps {
  detectionData: Detection;
  suricataRuleText: string;
}

export const DetectionAlerts = ({ detectionData }: DetectionAlertsProps) => {
  const { signOut } = useUserSession();

  const { metadataLoading, metadataData } =
    useOutletContext<IDetectionContext>();

  const {
    setPortalType,
    setSlideDrawerBody,
    setSlideDrawerTitle,
    setIsSlideDrawerOpen,
    setSlideDrawerOnClose,
    setActiveAlert,
  } = useContext(SlideDrawerContext);

  const { controls, alertActivity, alertsActivity } = i18n.pages.detections;

  const { logSearch } = ROUTES;

  const { start, end, sevenDayStart, sevenDayEnd } = useDateRangeQueryString();

  // Use URL params if valid, otherwise fallback to stable 7-day values from hook
  const validStart = start && !isNaN(start) ? start : sevenDayStart;
  const validEnd = end && !isNaN(end) ? end : sevenDayEnd;

  const {
    alert_entity,
    alert_info,
    earliest_start_timestamp,
    latest_start_timestamp,
    logscale_query,
    tenant,
    total_alert_count: totalAlertCount,
  } = detectionData || {};

  const detectionAlertType = detectionData?.alert_info?.alert_type;

  const { userPrivileges } = useContext(AuthContext);

  const isGPTPrivateDataEnabled = Boolean(
    userPrivileges?.[tenant]?.chatgpt_private_data,
  );

  const [offsetReset, setOffsetReset] = useState<boolean>(false);

  const {
    sortBy: alertsSortBy,
    pageSize: alertsPageSize,
    pageCount: alertsPageCount,
    pageIndex: alertsPageIndex,
    onFetchData: onFetchAlertsData,
    getPageCount: alertsGetPageCount,
    getSortsForAPI: alertsGetSortsForAPI,
    formatNestedSorts: alertsFormatNestSorts,
  } = useManualPagination<AlertSummary>({
    sortBy: [{ id: 'timestamp', desc: true }],
    offsetReset,
    setOffsetReset,
  });

  const query =
    detectionAlertType === 'ml'
      ? QUERY_ALERTS_ML_PAGINATED
      : detectionAlertType === 'notice'
        ? QUERY_ALERTS_NOTICE_PAGINATED
        : detectionAlertType === 'suricata_corelight'
          ? QUERY_ALERTS_SURICATA_PAGINATED
          : detectionAlertType === 'yara_corelight'
            ? QUERY_ALERTS_YARA_PAGINATED
            : detectionAlertType === 'anomaly'
              ? QUERY_ALERTS_ANOMALY_PAGINATED
              : detectionAlertType === 'custom_search_rule'
                ? QUERY_ALERTS_SEARCH_BASED_PAGINATED
                : QUERY_ALERTS_SEARCH_BASED_PAGINATED;

  const alertsSortedTimestamp = alertsFormatNestSorts(alertsSortBy);

  const [filters, setFilters] = useState<
    { id: string; value: string }[] | null
  >(null);

  const { isNewSearch } = useIsNewSearch({
    filters,
  });

  const [alertsSize, setAlertsSize] = useState<number>(10);
  const [alertsOffset, setAlertsOffset] = useState<number>(0);

  const noFilterVariables = {
    size: alertsSize,
    offset: isNewSearch ? 0 : alertsOffset,
    sort: alertsGetSortsForAPI(alertsSortedTimestamp),
    query: `{"query": {"bool": {"must": [{"term": {"alert_info.content_id": "${
      detectionData?.alert_info?.content_id
    }"}}, {"term": {"alert_entity.entity_id": "${
      detectionData?.alert_entity?.entity_id
    }"}}, {"range": {"alert_timestamp.end": {"gte": ${
      detectionData?.earliest_start_timestamp
    }}}}, {"range": {"alert_timestamp.start": {"lte": ${
      detectionData?.latest_start_timestamp
    }}}}${detectionData?.tenant_info?.tenant_id ? ',' : ''}${
      detectionData?.tenant_info?.tenant_id
        ? `{"terms":{"tenant":["${detectionData?.tenant_info?.tenant_id}"]}}`
        : ''
    }]}}}`,
  };

  const [_, setSelectedItem] = useState<
    | CustomSearchRuleAlert
    | MlAlert
    | NoticeAlert
    | SuricataAlert
    | YaraAlert
    | AnomalyAlert
  >();

  const {
    loading: alertsLoading,
    error: alertsError,
    data: alertsData,
    refetch: alertsRefetch,
  } = useQuery<IQueryAlertsPaginated>(query, {
    skip:
      !detectionData ||
      !detectionData?.tenant_info?.tenant_id ||
      !detectionData?.alert_entity?.entity_id,
    variables: noFilterVariables,
    onCompleted: (data) => {
      alertsGetPageCount(data?.queryAlertsPaginated);
      setSelectedItem(
        (data?.queryAlertsPaginated?.alerts?.[0] as
          | CustomSearchRuleAlert
          | MlAlert
          | NoticeAlert
          | SuricataAlert
          | YaraAlert
          | AnomalyAlert) ?? {},
      );
    },
  });

  const handleColFilters = (filters: DetectionAlertsFilters) => {
    setFilters(filters ?? null);
    if (filters.length) {
      alertsRefetch({
        ...noFilterVariables,
        offset: 0,
        query: `{"query": {"bool": {"must": [{"term": {"alert_info.content_id": "${
          detectionData?.alert_info?.content_id
        }"}}, {"term": {"alert_entity.entity_id": "${
          detectionData?.alert_entity?.entity_id
        }"}}${filters?.length > 0 ? ',' : ''}${filters
          .map((filter: { id: string; value: string }) =>
            filter.id === 'source_entities'
              ? `{"nested": {
                "path": "related_alert_entities",
                "query": {
                  "bool": {
                    "must": [
                      {"term": {"related_alert_entities.entity_category": {"value": "source"}}},
                      {"match_phrase": {"related_alert_entities.entity_name": {"query": "${filter.value}", "analyzer": "keyword"}}}
                    ]
                  }
                }
              }}`
              : filter.id === 'destination_entities'
                ? `{"nested": {
                "path": "related_alert_entities",
                "query": {
                  "bool": {
                    "must": [
                      {"term": {"related_alert_entities.entity_category": {"value": "destination"}}},
                      {"match_phrase": {"related_alert_entities.entity_name": {"query": "${filter.value}", "analyzer": "keyword"}}}
                    ]
                  }
                }
              }}`
                : '',
          )
          .join(',')},{"range": {"alert_timestamp.end": {"gte": ${
          detectionData?.earliest_start_timestamp
        }}}}, {"range": {"alert_timestamp.start": {"lte": ${
          detectionData?.latest_start_timestamp
        }}}}${detectionData?.tenant_info?.tenant_id ? ',' : ''}${
          detectionData?.tenant_info?.tenant_id
            ? `{"terms":{"tenant":["${detectionData?.tenant_info?.tenant_id}"]}}`
            : ''
        }]}}}`,
      });
    } else {
      alertsRefetch({
        ...noFilterVariables,
      });
    }
  };

  const handleFetchRelatedAlerts = useCallback(
    (pageIndex: number, pageSize: number, sortBy: SortingRule<Alert>[]) => {
      setAlertsSize(pageSize);
      setAlertsOffset(pageIndex * pageSize);
      onFetchAlertsData(pageIndex, pageSize, sortBy);
    },
    [onFetchAlertsData],
  );

  const widgetType = logscale_query ? 'table-view' : 'list-view';

  const query_string =
    metadataData?.getAlertMetadata?.metadata[0]?.custom_search_rule
      ?.query_string;

  const queryStringSearchBased = `?query=${
    logscale_query
      ? logscale_query && encodeURIComponent(logscale_query)
      : `${query_string && encodeURIComponent(query_string)}`
  }&live=false&humioStart=${
    earliest_start_timestamp === latest_start_timestamp
      ? ((earliest_start_timestamp ?? 0) - 60) * 1000
      : (earliest_start_timestamp ?? 0) * 1000
  }&humioEnd=${
    latest_start_timestamp === earliest_start_timestamp
      ? ((latest_start_timestamp ?? 0) + 60) * 1000
      : (latest_start_timestamp ?? 0) * 1000
  }&fullscreen=false&widgetType=${widgetType}&newestAtBottom=true&showOnlyFirstLine=false&start=${validStart}&end=${validEnd}`;

  const totalItems = alertsData?.queryAlertsPaginated?.total_items;

  const alertType =
    alertsData?.queryAlertsPaginated?.alerts[0]?.alert_info?.alert_type;

  // Ensure displayCountPrefix is always a string
  const displayCountPrefix =
    totalAlertCount > 10000
      ? String(
          i18n.formatString(
            i18n.pages.detections.alertsShowingLatest,
            '10000',
            String(totalAlertCount),
          ),
        )
      : String(totalAlertCount);

  const entityName = alert_entity?.entity_name ?? '';
  const alertName = alert_info?.alert_name ?? '';
  const latestStart = moment
    .unix(latest_start_timestamp ?? 0)
    .format('MMMM Do, h:mma');
  const earliestStart = moment
    .unix(earliest_start_timestamp ?? 0)
    .format('MMMM Do, h:mma');

  const alertsTabDescription =
    totalAlertCount === 1
      ? i18n.formatString(
          alertActivity,
          displayCountPrefix,
          entityName,
          alertName,
          latestStart,
        )
      : i18n.formatString(
          alertsActivity,
          displayCountPrefix,
          entityName,
          alertName,
          earliestStart,
          latestStart,
        );
  const alerts = alertsData?.queryAlertsPaginated?.alerts;

  const sanitizedNoticeAlerts: Partial<NoticeAlert | Detection>[] = (
    alerts as NoticeAlert[]
  )?.map(
    ({
      alert_id,
      uid,
      msg,
      alert_info,
      alert_entity,
      source_entities,
      destination_entities,
      alert_timestamp,
      tenant,
    }) => {
      return {
        alert_id,
        uid,
        msg,
        alert_info,
        alert_entity,
        logscale_query,
        alert_timestamp,
        source_entities,
        destination_entities,
        timestamp: alert_timestamp?.observed,
        tenant,
      };
    },
  );

  const sanitizedSuricataAlerts: Partial<SuricataAlert | Detection>[] = (
    alerts as SuricataAlert[]
  )?.map(
    ({
      alert_id,
      uid,
      alert_info,
      alert_entity,
      community_id,
      destination_ip,
      source_entities,
      destination_entities,
      alert_timestamp,
      tenant,
    }) => {
      return {
        alert_id,
        uid,
        alert_info,
        alert_entity,
        logscale_query,
        alert_timestamp,
        community_id,
        destination_ip,
        source_entities,
        destination_entities,
        timestamp: alert_timestamp?.observed,
        tenant,
      };
    },
  );

  const sanitizedMlAlerts: Partial<MlAlert | Detection>[] = (
    alerts as MlAlert[]
  )?.map(
    ({
      models,
      pipeline,
      alert_info,
      alert_entity,
      alert_timestamp,
      source_entities,
      related_entities,
      destination_entities,
      average_technique_score_map,
    }) => {
      return {
        models,
        pipeline,
        alert_info,
        alert_entity,
        logscale_query,
        alert_timestamp,
        related_entities,
        average_technique_score_map,
        source_entities,
        destination_entities,
        timestamp: alert_timestamp?.observed,
      };
    },
  );

  const sanitizedSearchBasedAlerts: Partial<CustomSearchRuleAlert>[] = (
    alerts as CustomSearchRuleAlert[]
  )?.map(
    ({
      alert_info,
      alert_entity,
      alert_timestamp,
      source_entities,
      destination_entities,
    }) => {
      return {
        alert_info,
        alert_entity,
        logscale_query,
        alert_timestamp,
        source_entities,
        destination_entities,
        timestamp: alert_timestamp?.observed,
      };
    },
  );

  const sanitizedYaraAlerts: Partial<SanitizedYaraAlert | Detection>[] = (
    alerts as YaraAlert[]
  )?.map(
    ({
      alert_id,
      uid,
      alert_info,
      alert_entity,
      alert_timestamp,
      source_entities,
      destination_entities,
      tenant,
      match_meta,
    }) => {
      return {
        alert_id,
        uid,
        alert_info,
        alert_entity,
        alert_timestamp,
        logscale_query: logscale_query ?? undefined,
        source_entities: Array.isArray(source_entities)
          ? source_entities.filter((e): e is AlertEntity => !!e)
          : undefined,
        destination_entities: Array.isArray(destination_entities)
          ? destination_entities.filter((e): e is AlertEntity => !!e)
          : undefined,
        timestamp:
          alert_timestamp?.observed !== null &&
          alert_timestamp?.observed !== undefined
            ? alert_timestamp.observed
            : undefined,
        tenant,
        match_meta,
      };
    },
  );

  const sanitizedAnomalyAlerts: Partial<SanitizedAnomalyAlert>[] = (
    alerts as AnomalyAlert[]
  )?.map(
    ({
      alert_id,
      uid,
      alert_info,
      alert_entity,
      alert_timestamp,
      source_entities,
      destination_entities,
      path,
      system_name,
      orig_h,
      source_ip,
      usecase,
      usecase_description,
      anomaly_type,
      tenant,
    }) => {
      return {
        alert_id,
        uid,
        alert_info,
        alert_entity,
        alert_timestamp,
        source_entities: Array.isArray(source_entities)
          ? source_entities.filter((e): e is AlertEntity => !!e)
          : undefined,
        destination_entities: Array.isArray(destination_entities)
          ? destination_entities.filter((e): e is AlertEntity => !!e)
          : undefined,
        timestamp:
          alert_timestamp?.observed !== null &&
          alert_timestamp?.observed !== undefined
            ? alert_timestamp.observed
            : undefined,
        path,
        system_name,
        orig_h,
        source_ip,
        usecase,
        usecase_description,
        anomaly_type,
        tenant,
      };
    },
  );

  const detectionSummary = useMemo(() => {
    if (!detectionData) return undefined;

    // Use factory to create normalized DetectionSummary
    return getDetectionSummary(detectionData);
  }, [detectionData]);

  const mlDataTableColumns: TableColumn<SanitizedMlAlert>[] = useMemo(
    () => [
      {
        Header: 'Timestamp',
        accessor: 'timestamp',
        Cell: (props) => {
          return <TimestampCell timestamp={props.value} isFromNow={false} />;
        },
      },
      {
        Header: 'Source',
        accessor: 'source_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const sourceInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                sourceInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Destination',
        accessor: 'destination_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const destinationInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                destinationInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'logscale_query',
        disableSortBy: true,
        Cell: (props) => {
          const {
            models,
            pipeline,
            alert_info,
            alert_entity,
            alert_timestamp,
            related_entities,
            average_technique_score_map,
          } = props.row.original;
          return (
            <MLButtonsCol
              item={{
                models,
                pipeline,
                alert_info,
                alert_entity,
                alert_timestamp,
                related_entities,
                average_technique_score_map,
              }}
              logScaleQuery={props.value}
            />
          );
        },
      },
    ],
    [detectionSummary, validStart, validEnd],
  );

  const searchBasedDataTableColumns: TableColumn<SanitizedSearchBasedAlert>[] =
    useMemo(
      () => [
        {
          Header: 'Timestamp',
          accessor: 'timestamp',
          Cell: (props) => {
            return <TimestampCell timestamp={props.value} isFromNow={false} />;
          },
        },
        {
          Header: 'Source',
          accessor: 'source_entities',
          disableSortBy: true,
          Filter: ColumnFilterPopover,
          Cell: (props) => {
            const sourceInfo = props?.value?.[0];
            return (
              <AlertEntityPopover
                entities={props?.value}
                detectionSummary={getDetectionSummary(
                  detectionData,
                  sourceInfo as Partial<AlertEntity> | undefined,
                )}
                start={validStart}
                end={validEnd}
              />
            );
          },
        },
        {
          Header: 'Destination',
          accessor: 'destination_entities',
          disableSortBy: true,
          Filter: ColumnFilterPopover,
          Cell: (props) => {
            const destinationInfo = props?.value?.[0];
            return (
              <AlertEntityPopover
                entities={props?.value}
                detectionSummary={getDetectionSummary(
                  detectionData,
                  destinationInfo as Partial<AlertEntity> | undefined,
                )}
                start={validStart}
                end={validEnd}
              />
            );
          },
        },
      ],
      [detectionSummary, validStart, validEnd],
    );

  const noticeDataTableColumns: TableColumn<SanitizedNoticeAlert>[] = useMemo(
    () => [
      {
        Header: 'Timestamp',
        accessor: 'timestamp',
        Cell: (props) => {
          return (
            props?.value && (
              <TimestampCell timestamp={props.value} isFromNow={false} />
            )
          );
        },
      },
      {
        Header: 'Source',
        accessor: 'source_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const sourceInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                sourceInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Destination',
        accessor: 'destination_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const destinationInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                destinationInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'logscale_query',
        disableSortBy: true,
        Cell: (props) => {
          const {
            msg,
            uid,
            alert_info,
            alert_entity,
            alert_timestamp,
            tenant,
          } = props.row.original;

          return (
            <NoticeButtonsCol
              item={
                {
                  uid: uid ?? '',
                  msg,
                  alert_info,
                  alert_entity,
                  alert_timestamp,
                  tenant,
                } as SanitizedNoticeAlert
              }
              logScaleQuery={props.value}
            />
          );
        },
      },
    ],
    [detectionSummary, validStart, validEnd],
  );

  const suricataDataTableColumns: TableColumn<SanitizedSuricataAlert>[] =
    useMemo(
      () => [
        {
          Header: 'Timestamp',
          accessor: 'timestamp',
          Cell: (props) => {
            return (
              props?.value && (
                <TimestampCell timestamp={props.value} isFromNow={false} />
              )
            );
          },
        },
        {
          Header: 'Source',
          accessor: 'source_entities',
          disableSortBy: true,
          Filter: ColumnFilterPopover,
          Cell: (props) => {
            const sourceInfo = props?.value?.[0];
            return (
              <AlertEntityPopover
                entities={props?.value}
                detectionSummary={getDetectionSummary(
                  detectionData,
                  sourceInfo as Partial<AlertEntity> | undefined,
                )}
                start={validStart}
                end={validEnd}
              />
            );
          },
        },
        {
          Header: 'Destination',
          accessor: 'destination_entities',
          disableSortBy: true,
          Filter: ColumnFilterPopover,
          Cell: (props) => {
            const destinationInfo = props?.value?.[0];
            return (
              <AlertEntityPopover
                entities={props?.value}
                detectionSummary={getDetectionSummary(
                  detectionData,
                  destinationInfo as Partial<AlertEntity> | undefined,
                )}
                start={validStart}
                end={validEnd}
              />
            );
          },
        },
        {
          Header: 'Actions',
          accessor: 'logscale_query',
          disableSortBy: true,
          Cell: (props) => {
            const {
              uid,
              alert_info,
              alert_entity,
              community_id,
              destination_ip,
              alert_timestamp,
              tenant,
            } = props.row.original;

            return (
              <SuricataButtonsCol
                item={
                  {
                    uid: uid ?? '',
                    alert_info,
                    alert_entity,
                    community_id,
                    destination_ip,
                    alert_timestamp,
                    tenant,
                  } as SanitizedSuricataAlert
                }
                logScaleQuery={props.value}
              />
            );
          },
        },
      ],
      [detectionSummary, validStart, validEnd],
    );

  const yaraDataTableColumns: TableColumn<SanitizedYaraAlert>[] = useMemo(
    () => [
      {
        Header: 'Timestamp',
        accessor: 'timestamp',
        Cell: (props) => (
          <TimestampCell timestamp={props.value} isFromNow={false} />
        ),
      },
      {
        Header: 'Source',
        accessor: 'source_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const sourceInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                sourceInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Destination',
        accessor: 'destination_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const destinationInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                destinationInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'logscale_query',
        disableSortBy: true,
        Cell: (props) => (
          <YaraButtonsCol
            item={props.row.original as SanitizedYaraAlert}
            logScaleQuery={props.value}
          />
        ),
      },
    ],
    [detectionSummary, validStart, validEnd],
  );

  const anomalyDataTableColumns: TableColumn<SanitizedAnomalyAlert>[] = useMemo(
    () => [
      {
        Header: 'Timestamp',
        accessor: 'timestamp',
        Cell: (props) => (
          <TimestampCell timestamp={props.value} isFromNow={false} />
        ),
      },
      {
        Header: 'Source',
        accessor: 'source_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const sourceInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                sourceInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Destination',
        accessor: 'destination_entities',
        disableSortBy: true,
        Filter: ColumnFilterPopover,
        Cell: (props) => {
          const destinationInfo = props?.value?.[0];
          return (
            <AlertEntityPopover
              entities={props?.value}
              detectionSummary={getDetectionSummary(
                detectionData,
                destinationInfo as Partial<AlertEntity> | undefined,
              )}
              start={validStart}
              end={validEnd}
            />
          );
        },
      },
      {
        Header: 'Actions',
        accessor: 'path',
        disableSortBy: true,
        Cell: (props) => (
          <AnomalyButtonsCol
            item={{
              ...(props.row.original as SanitizedAnomalyAlert),
              alert_name: props.row.original.alert_info?.alert_name ?? '',
              severity:
                (props.row.original as SanitizedAnomalyAlert).severity ?? 0,
              is_custom_severity:
                (props.row.original as SanitizedAnomalyAlert)
                  .is_custom_severity ?? false,
            }}
          />
        ),
      },
    ],
    [detectionSummary, validStart, validEnd],
  );

  useEffect(() => {
    setSlideDrawerOnClose?.(() => () => {
      setActiveAlert?.(null);
    });
  }, [setSlideDrawerOnClose, setActiveAlert]);

  if (
    (detectionData?.alert_info?.alert_type === 'ml' && !sanitizedMlAlerts) ||
    (detectionData?.alert_info?.alert_type === 'notice' &&
      !sanitizedNoticeAlerts) ||
    (detectionData?.alert_info?.alert_type === 'custom_search_rule' &&
      !sanitizedSearchBasedAlerts) ||
    (detectionData?.alert_info?.alert_type === 'suricata_corelight' &&
      !sanitizedSuricataAlerts) ||
    (detectionData?.alert_info?.alert_type === 'yara_corelight' &&
      !sanitizedYaraAlerts) ||
    (detectionData?.alert_info?.alert_type === 'anomaly' &&
      !sanitizedAnomalyAlerts)
  ) {
    return (
      <Panel>
        <GraphQLReqStatus loading={true} />
      </Panel>
    );
  }

  return alertsError ? (
    <Panel>
      <GraphQLReqStatus
        loading={false}
        isBackground={true}
        error={alertsError}
      />
    </Panel>
  ) : (
    <Panel>
      <FlushPanelContent>
        <VStack
          w="100%"
          spacing={0}
          alignItems="start"
          data-testid="detection-alerts"
        >
          <Flex
            px={4}
            py={5}
            w="100%"
            bg="layer.2"
            alignItems="center"
            justifyContent="space-between"
            borderBottomWidth="1px"
            borderBottomColor="white.300"
          >
            {!!totalAlertCount && (
              <Text
                data-testid={`${alertType}-alerts-tab-description`}
                textStyle="b1"
              >
                {alertsTabDescription}
                {totalAlertCount > 10000 && (
                  <Tooltip
                    label={i18n.formatString(
                      i18n.pages.detections.alertsPerformanceTooltip,
                      '10000',
                    )}
                    placement="top"
                    hasArrow
                    shouldWrapChildren
                  >
                    <Box
                      as="span"
                      ml={1}
                      cursor="pointer"
                      display="inline-flex"
                      alignItems="center"
                    >
                      <InfoSharpIcon />
                    </Box>
                  </Tooltip>
                )}
              </Text>
            )}
            {detectionData?.alert_info?.alert_type === 'custom_search_rule' && (
              <Button
                size="sm"
                minWidth="auto"
                as={RouterLink}
                variant="solid"
                disabled={metadataLoading}
                data-testid="investigate-logs-button"
                leftIcon={
                  metadataLoading ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <FrameInspectSharpIcon />
                  )
                }
                to={`${logSearch}${queryStringSearchBased}`}
              >
                {
                  i18n.pages.alertDetails.panels.entityDetails.buttons
                    .investigateLogs
                }
              </Button>
            )}
          </Flex>
          <Box width="100%">
            <Panel border={0}>
              <FlushPanelContent>
                {detectionData?.alert_info?.alert_type && (
                  <DataTable
                    isMemod={false}
                    hideColumnHeaders={alertsLoading}
                    isPaginable={Boolean(
                      !alertsLoading && !alertsError && totalItems,
                    )}
                    isFilterManual={true}
                    onColumnFilter={handleColFilters}
                    autoResetPage={alertsOffset / alertsSize >= alertsPageCount}
                    pageSize={alertsPageSize}
                    pageCount={alertsPageCount}
                    // autoResetPage={false}
                    autoResetSortBy={false}
                    autoResetRowSelection={false}
                    isPaginationManual={true}
                    defaultColumn={{ width: 150 }}
                    // @ts-expect-error DataTable expects a different columns type
                    columns={
                      detectionData?.alert_info?.alert_type === 'ml'
                        ? mlDataTableColumns
                        : detectionData?.alert_info?.alert_type === 'notice'
                          ? noticeDataTableColumns
                          : detectionData?.alert_info?.alert_type ===
                              'suricata_corelight'
                            ? suricataDataTableColumns
                            : detectionData?.alert_info?.alert_type ===
                                'custom_search_rule'
                              ? searchBasedDataTableColumns
                              : detectionData?.alert_info?.alert_type ===
                                  'yara_corelight'
                                ? yaraDataTableColumns
                                : detectionData?.alert_info?.alert_type ===
                                    'anomaly'
                                  ? anomalyDataTableColumns
                                  : []
                    }
                    onFetchData={handleFetchRelatedAlerts}
                    itemCount={
                      alertsData?.queryAlertsPaginated?.total_items || 0
                    }
                    emptyBodyContent={
                      <Center m={4}>{controls.noResults}</Center>
                    }
                    // @ts-expect-error DataTable expects a different data type
                    data={
                      detectionData?.alert_info?.alert_type === 'ml'
                        ? (sanitizedMlAlerts as SanitizedMlAlert[])
                        : detectionData?.alert_info?.alert_type === 'notice'
                          ? (sanitizedNoticeAlerts as SanitizedNoticeAlert[])
                          : detectionData?.alert_info?.alert_type ===
                              'suricata_corelight'
                            ? (sanitizedSuricataAlerts as SanitizedSuricataAlert[])
                            : detectionData?.alert_info?.alert_type ===
                                'yara_corelight'
                              ? (sanitizedYaraAlerts as SanitizedYaraAlert[])
                              : detectionData?.alert_info?.alert_type ===
                                  'custom_search_rule'
                                ? (sanitizedSearchBasedAlerts as SanitizedSearchBasedAlert[])
                                : detectionData?.alert_info?.alert_type ===
                                    'anomaly'
                                  ? (sanitizedAnomalyAlerts as SanitizedAnomalyAlert[])
                                  : []
                    }
                    initialState={
                      {
                        pageIndex: offsetReset ? 0 : alertsPageIndex,
                        sortBy: alertsSortBy,
                      } as Partial<TableState<AlertSummary>>
                    }
                    onRowClick={({ original }, event) => {
                      if (
                        (event.target as HTMLElement)?.className?.includes &&
                        !['icon', 'link', 'menu', 'button', 'switch'].some(
                          (classNameString) =>
                            (event.target as HTMLElement).className.includes(
                              classNameString,
                            ),
                        )
                      ) {
                        setSlideDrawerTitle?.(undefined); // Empty title for alerts
                        setPortalType?.('alert');
                        // Show loading or error state if needed
                        if (alertsLoading || alertsError) {
                          setSlideDrawerBody?.(
                            <Center p={8}>
                              <GraphQLReqStatus
                                loading={alertsLoading}
                                error={alertsError}
                              />
                            </Center>,
                          );
                          setIsSlideDrawerOpen?.(true);
                          setActiveAlert?.(original);
                          return;
                        }

                        // Set alert-specific content based on alert type
                        const alertContent = (() => {
                          const commonProps = {
                            loading: alertsLoading,
                            detectionData,
                            isGPTPrivateDataEnabled,
                            logScaleQuery: logscale_query || '', // Use actual logscale query
                          };

                          switch (original?.alert_info?.alert_type) {
                            case 'ml':
                              return (
                                <AlertRowSplitML
                                  item={original as unknown as SanitizedMlAlert}
                                  loading={commonProps.loading}
                                  logScaleQuery={commonProps.logScaleQuery}
                                />
                              );
                            case 'notice':
                              return (
                                <AlertRowSplitNotice
                                  item={
                                    original as unknown as SanitizedNoticeAlert
                                  }
                                  loading={commonProps.loading}
                                  isGPTPrivateDataEnabled={
                                    commonProps.isGPTPrivateDataEnabled
                                  }
                                  logScaleQuery={commonProps.logScaleQuery}
                                />
                              );
                            case 'suricata_corelight':
                              return (
                                <AlertRowSplitSuricata
                                  item={
                                    original as unknown as SanitizedSuricataAlert
                                  }
                                  loading={commonProps.loading}
                                  suricataRuleText={''}
                                  isGPTPrivateDataEnabled={
                                    commonProps.isGPTPrivateDataEnabled
                                  }
                                  logScaleQuery={commonProps.logScaleQuery}
                                />
                              );
                            case 'yara_corelight':
                              return (
                                <AlertRowSplitYara
                                  item={
                                    original as unknown as SanitizedYaraAlert
                                  }
                                  loading={commonProps.loading}
                                  isGPTPrivateDataEnabled={
                                    commonProps.isGPTPrivateDataEnabled
                                  }
                                  logScaleQuery={commonProps.logScaleQuery}
                                />
                              );
                            case 'custom_search_rule':
                              return (
                                <AlertRowSplitSearchBased
                                  item={
                                    original as unknown as SanitizedSearchBasedAlert
                                  }
                                  loading={commonProps.loading}
                                />
                              );
                            case 'anomaly':
                              return (
                                <AlertRowSplitAnomaly
                                  item={
                                    original as unknown as SanitizedAnomalyAlert
                                  }
                                  loading={commonProps.loading}
                                  isGPTPrivateDataEnabled={
                                    commonProps.isGPTPrivateDataEnabled
                                  }
                                  logScaleQuery={commonProps.logScaleQuery}
                                />
                              );
                            default:
                              return null;
                          }
                        })();

                        setSlideDrawerBody?.(alertContent);
                        setIsSlideDrawerOpen?.(true);
                        setActiveAlert?.(original);
                      }
                    }}
                    footer={DetectionAlertsFooter({
                      loading: alertsLoading,
                      error: alertsError,
                      data: alertsData?.queryAlertsPaginated?.alerts,
                      signOut,
                    })}
                  />
                )}
              </FlushPanelContent>
            </Panel>
          </Box>
        </VStack>
      </FlushPanelContent>
    </Panel>
  );
};
